// src/utils/helpers.js

import { format, subDays, eachDayOfInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachMonthOfInterval, startOfYear, endOfYear } from "date-fns";

/** Get the Sunday to Saturday range for a given date */
export const getWeekRange = (date = new Date()) => {
  const start = startOfWeek(new Date(date), { weekStartsOn: 0 }); // 0 is Sunday
  const end = endOfWeek(new Date(date), { weekStartsOn: 0 });
  return eachDayOfInterval({ start, end }).map(d => format(d, "yyyy-MM-dd"));
};

/** Get all dates in the month of the given date */
export const getMonthRange = (date = new Date()) => {
  const start = startOfMonth(new Date(date));
  const end = endOfMonth(new Date(date));
  return eachDayOfInterval({ start, end }).map(d => format(d, "yyyy-MM-dd"));
};

/** Get all months in the year of the given date */
export const getYearMonths = (date = new Date()) => {
  const start = startOfYear(new Date(date));
  const end = endOfYear(new Date(date));
  return eachMonthOfInterval({ start, end }).map(d => format(d, "yyyy-MM"));
};

/** Format a date string to readable format */
export const formatDate = (dateStr) =>
  format(new Date(dateStr + "T00:00:00"), "dd MMM yyyy");

/** Today's date as YYYY-MM-DD */
export const todayStr = () => new Date().toISOString().split("T")[0];

/** Current time as HH:MM */
export const nowTime = () => new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

/** Convert minutes to HH:MM format */
export const minutesToHHMM = (minutes) => {
  if (!minutes && minutes !== 0) return "00:00";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Convert HH:MM string to total minutes */
export const HHMMToMinutes = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Get initials from a full name */
export const getInitials = (name = "") =>
  name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

/** Calculate working days count (excluding Sundays) between two dates */
export const getWorkingDaysCount = (start, end = new Date()) => {
  if (!start) return 0;
  try {
    const startDate = new Date(start);
    const endDate   = new Date(end);
    if (startDate > endDate) return 0;
    
    return eachDayOfInterval({ start: startDate, end: endDate })
      .filter(d => d.getDay() !== 0).length; // 0 is Sunday
  } catch (e) {
    console.error("Error calculating working days:", e);
    return 0;
  }
};

/** Get last N working days (excluding Sundays) as YYYY-MM-DD strings */
export const getLastNWorkingDays = (n = 7) => {
  const dates = [];
  let d = new Date();
  while (dates.length < n) {
    if (d.getDay() !== 0) { // Not Sunday
      dates.unshift(format(d, "yyyy-MM-dd"));
    }
    d = subDays(d, 1);
  }
  return dates;
};

/** Get last N days as YYYY-MM-DD strings */
export const getLastNDays = (n = 30) => {
  const today = new Date();
  return eachDayOfInterval({ start: subDays(today, n - 1), end: today })
    .map(d => format(d, "yyyy-MM-dd"));
};

/** Calculate attendance rate for a student given records map and date list */
export const calcRate = (records = {}, dates = []) => {
  if (!dates.length) return 0;
  const present = dates.filter(d => records[d]?.status === "present").length;
  return Math.round((present / dates.length) * 100);
};

/** Status color mapping */
export const statusColor = (status) => ({
  present: "#16A349",
  absent:  "#DC2626",
  late:    "#D97706",
}[status] || "#6B7280");

/** Generate a student ID from roll number */
export const makeStudentId = (roll) =>
  `S${String(roll).padStart(3, "0")}`;

/** Download a Blob as a file */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

/** Send WhatsApp message via local gateway */
export const sendWhatsAppMessage = async (phone, message, mediaBase64 = null) => {
  if (!phone || !phone.trim()) return { success: false, error: "No phone number" };
  
  const formData = new FormData();
  formData.append('numbers', phone);
  formData.append('speed', 'high');
  
  if (mediaBase64) {
    formData.append('filecaption', message);
    try {
      const response = await fetch(mediaBase64);
      const blob = await response.blob();
      formData.append('mediaFile', blob, 'attendance.jpg');
    } catch (e) {
      console.error("Error converting photo for WhatsApp", e);
    }
  } else {
    formData.append('message', message);
  }

  try {
    const response = await fetch('http://Sonias-MacBook-Air.local:3001/send', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    return { success: !!data.success, error: data.error || null, mode: data.mode };
  } catch (error) {
    console.error("WhatsApp Send Error:", error);
    return { success: false, error: error.message };
  }
};
